"use client";

import type { TenantIdentity } from "@/config/tenants";
import { useAppLocale } from "@/features/localization/app-locale-provider";
import {
  refundCopy,
  type RefundSection,
} from "@/features/legal/content/refund-copy";
import { LegalPageHeader } from "@/features/legal/components/legal-page-header";

export function RefundPageContent({ tenant }: Readonly<{ tenant: TenantIdentity }>) {
  const { locale } = useAppLocale();
  const copy = refundCopy[locale];

  return (
    <main className="bg-muted/40 px-4 py-10 sm:px-6 sm:py-14">
      <article className="mx-auto max-w-5xl rounded-2xl border border-border bg-card px-5 py-7 text-card-foreground shadow-sm sm:px-10 sm:py-10 lg:px-14">
        <LegalPageHeader tenant={tenant} />

        <div className="py-10 sm:py-12">
          <p className="text-sm font-bold uppercase tracking-wider text-primary">
            {copy.version}
          </p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
            {copy.title}
          </h1>
          <p className="mt-7 border-l-4 border-primary px-5 py-3 leading-7 text-muted-foreground">
            {copy.precedence}
          </p>
        </div>

        <div>
          {copy.sections.map((section) => (
            <RefundSectionView key={section.title} section={section} />
          ))}
        </div>
      </article>
    </main>
  );
}

function RefundSectionView({ section }: { section: RefundSection }) {
  return (
    <section className="border-t border-border py-8">
      <h2 className="text-2xl font-bold tracking-tight">{section.title}</h2>
      {section.paragraphs?.map((paragraph) => (
        <p className="mt-4 leading-7 text-muted-foreground" key={paragraph}>
          {paragraph}
        </p>
      ))}
    </section>
  );
}
